// Builds the cycle-by-cycle performance history shown in the CVI trend.
//
// Source-of-truth rules (see docs/CVI_Canada_Dashboard_Enhancements.md):
// - Closed cycles use the stored PerformanceSnapshot (latest version). They are never recalculated.
// - The active cycle uses the server CVI endpoint (/api/cvi/periods/:id/me), the same calculation
//   the finalization service stores. The browser never estimates a score: when the server has no
//   CVI for the active cycle (no data yet, or the request failed) the cycle is simply not plotted.
// - Cycles are ordered chronologically by period start date.

const toNumber = value => (value == null || value === "" || !Number.isFinite(Number(value)) ? null : Number(value));

export function normalizeSnapshotScores(snapshot) {
    return {
        individual: toNumber(snapshot?.individualScore),
        org: toNumber(snapshot?.organizationalScore),
        cvi: toNumber(snapshot?.cviScore),
    };
}

export function historicalCviAverage(rows = []) {
    const available = rows.map(row => toNumber(row?.cvi)).filter(value => value != null);
    return available.length ? available.reduce((sum, value) => sum + value, 0) / available.length : null;
}

// A missing component must split a trend instead of being joined across a gap.
export function consecutiveScoreRuns(periods = [], key) {
    const runs = [];
    let current = [];
    periods.forEach((period, index) => {
        const value = toNumber(period?.[key]);
        if (value == null) {
            if (current.length >= 2) runs.push(current);
            current = [];
        } else {
            current.push({ index, value });
        }
    });
    if (current.length >= 2) runs.push(current);
    return runs;
}

export function latestSnapshotsByPeriod(snapshots = [], userId) {
    const latest = {};
    for (const snapshot of snapshots || []) {
        if (snapshot.userId !== userId) continue;
        const current = latest[snapshot.periodId];
        if (!current || Number(snapshot.version || 1) >= Number(current.version || 1)) latest[snapshot.periodId] = snapshot;
    }
    return Object.values(latest);
}

// Breakdown details carried by the server result, in the same shape the stored snapshots use.
function serverBreakdown(payload) {
    const detail = {};
    const groups = payload?.breakdown?.nonRoutine?.groups;
    if (Array.isArray(groups)) {
        detail.activityGroupScores = Object.fromEntries(groups.map(group => [group.groupId, toNumber(group.score)]));
        detail.activityCounts = Object.fromEntries(groups.map(group => [group.groupId, Number(group.activityCount) || 0]));
    }
    const questions = payload?.breakdown?.routine?.questions;
    if (Array.isArray(questions)) {
        detail.routineCriteria = questions
            .filter(question => toNumber(question.normalizedScore) != null)
            .map(question => ({ label: question.text, score: toNumber(question.normalizedScore), weight: toNumber(question.weight) }));
    }
    const split = payload?.weights?.tasksActivitiesSplit;
    const components = payload?.weights?.individualComponents;
    if (split || components) detail.weights = { nonRoutine: split?.nonContinuous ?? null, routine: split?.continuous ?? null, peerFeedback: components?.peerFeedback ?? null };
    return detail;
}

export function liveEntryFromServer(payload, activePeriod) {
    const scores = payload?.scores;
    if (!scores || !activePeriod?.id) return null;
    if (toNumber(scores.cvi) == null) return null;
    return {
        periodId: activePeriod.id,
        label: activePeriod.label,
        individual: toNumber(scores.individual),
        org: toNumber(scores.organizational),
        cvi: toNumber(scores.cvi),
        nonRoutine: toNumber(scores.nonRoutine),
        routine: toNumber(scores.routine),
        peerFeedback: toNumber(scores.peerFeedback),
        ...serverBreakdown(payload),
        status: payload.source === "live_calculation" ? "temporary" : String(payload.status || "final").toLowerCase(),
        locked: payload.source !== "live_calculation",
        source: payload.source === "live_calculation" ? "live" : "snapshot",
    };
}

export function buildPerformanceHistory({ snapshots = [], userId, periods = [], activePeriod = null, liveEntry = null }) {
    const startOf = periodId => {
        const period = (periods || []).find(item => String(item.id) === String(periodId));
        return String(period?.start || period?.startDate || "");
    };
    const activeId = activePeriod?.id ? String(activePeriod.id) : null;
    const rows = latestSnapshotsByPeriod(snapshots, userId)
        // The active cycle is represented once, by the live server value below.
        .filter(snapshot => !activeId || String(snapshot.periodId) !== activeId || snapshot.locked)
        .map(snapshot => ({ ...snapshot, source: snapshot.source || "snapshot" }));
    const hasActiveSnapshot = activeId && rows.some(row => String(row.periodId) === activeId);
    if (activeId && !hasActiveSnapshot) {
        const current = liveEntry;
        if (current) rows.push({ status: "temporary", locked: false, ...current, periodId: activePeriod.id, label: activePeriod.label, provisional: true });
    }
    return rows
        .map((row, index) => ({ row, index, start: startOf(row.periodId) }))
        .sort((a, b) => (a.start && b.start && a.start !== b.start ? a.start.localeCompare(b.start) : a.index - b.index))
        .map(item => item.row);
}
